import { useEffect, useState } from 'react';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

export default function AdminMatchingWeights() {
  const [weights, setWeights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/admin/matching-weights').then((r) => {
      setWeights(r.data.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function saveWeight(key, weight) {
    setSaving(key);
    try {
      await api.put(`/admin/matching-weights/${key}`, { weight: Number(weight) });
      setWeights((prev) => prev.map((w) => w.factor_key === key ? { ...w, weight: Number(weight) } : w));
      setEditing((prev) => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Weight updated');
    } finally { setSaving(null); }
  }

  const totalWeight = weights.reduce((sum, w) => sum + Number(w.weight), 0);

  if (loading) return <AdminLayout><div className="animate-pulse p-8">Loading weights...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Matching Weights</h1>
            <p className="text-sm text-gray-500 mt-0.5">Configure how each factor contributes to worker–job match scores</p>
          </div>
          <div className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${Math.abs(totalWeight - 1) < 0.01 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            Total: {(totalWeight * 100).toFixed(0)}% {Math.abs(totalWeight - 1) < 0.01 ? '✓' : '(should be 100%)'}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {weights.map((w) => (
            <div key={w.factor_key} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">{w.factor_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                {w.description && <p className="text-xs text-gray-500 mt-0.5">{w.description}</p>}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-24 bg-gray-200 rounded-full h-2 hidden sm:block">
                  <div className="bg-brand-500 h-2 rounded-full" style={{ width: `${Math.min(Number(w.weight) * 100, 100)}%` }} />
                </div>
                {editing[w.factor_key] !== undefined ? (
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" min="0" max="1"
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-20 focus:ring-2 focus:ring-brand-500 focus:outline-none"
                      value={editing[w.factor_key]}
                      onChange={(e) => setEditing((p) => ({ ...p, [w.factor_key]: e.target.value }))} />
                    <Button size="sm" loading={saving === w.factor_key} onClick={() => saveWeight(w.factor_key, editing[w.factor_key])}>Save</Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing((p) => { const n = { ...p }; delete n[w.factor_key]; return n; })}>✕</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono bg-gray-100 px-3 py-1.5 rounded-lg text-gray-800 w-16 text-center">{Number(w.weight).toFixed(2)}</span>
                    <Button size="sm" variant="secondary" onClick={() => setEditing((p) => ({ ...p, [w.factor_key]: w.weight }))}>Edit</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800 font-medium">About Matching Weights</p>
          <p className="text-xs text-blue-600 mt-1">
            Weights determine how heavily each factor is considered when matching workers to job requirements.
            All weights should sum to 1.00 (100%) for accurate scoring.
            Changes take effect on the next matching run.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
